import type { Coding } from "fhir/r4";

/**
 * FHIR `Coding` shape check.
 *
 * Coding has all-optional fields per FHIR R4, so any non-array object that arrived via a typed `valueCoding` /
 * `answerCoding` slot is treated as a Coding. Stricter ("must contain at least one Coding field") would reject
 * legitimate sparse Codings (e.g. text-only, or system-only references).
 *
 * @see https://hl7.org/fhir/R4/datatypes.html#Coding
 */
const isCoding = (value: unknown): value is Coding => {
	if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
	return true;
};

/**
 * Two Codings are considered equal only on full equality of `code`, `system`, and `version` — each field must be the
 * same on both sides, or absent on both sides. Asymmetric sparseness (e.g. one side has a `system`, the other doesn't)
 * is treated as a mismatch.
 *
 * Used for FHIR R4 `enableWhen` and `answerOption` membership checks.
 */
export const codingsMatch = (expected: unknown, actual: unknown): boolean =>
	isCoding(expected)
	&& isCoding(actual)
	&& expected.code === actual.code
	&& expected.system === actual.system
	&& expected.version === actual.version;
